import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Card,
  CardBody,
  Col,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Table
} from 'reactstrap';
import qs from 'qs';
import PropTypes from 'prop-types';
import axios from 'axios';
import LoadingIcon from 'mdi-react/LoadingIcon';
import AutorenewIcon from 'mdi-react/AutorenewIcon';
import { format } from 'date-fns';
import ptBR from 'date-fns/locale/pt-BR';
import { registerLocale } from 'react-datepicker';
import { getToken } from '../../../services/auth';
import { brMoneyMask } from '../../../shared/utils/utils';

registerLocale('ptBR', ptBR);

const HistoricTable = ({ inicialDate, finalDate }) => {
  const client = localStorage.getItem('client')
    ? JSON.parse(localStorage.getItem('client'))
    : '';
  const limit = 10;
  const [loading, setLoading] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);

  useEffect(() => {
    setCurrentPage(1);
  }, [inicialDate, finalDate]);

  const getHistoricData = useCallback(() => {
    setLoading(true);
    const url = `${process.env.REACT_APP_WAS_KEY}/wallet/payment/historic`;
    const startDate = inicialDate.split('/').reverse().join('-');
    const endDate = finalDate.split('/').reverse().join('-');
    axios
      .get(url, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
          'x-api-key': client.owner
        },
        params: { startDate, endDate, page: currentPage, limit },
        paramsSerializer(params) {
          return qs.stringify(params, { encode: false });
        }
      })
      .then((res) => {
        const rows = res.data?.rows ?? [];
        const pages = Number(res.data?.totalPages ?? 0);
        setTableData(rows);
        setTotalPages(pages);

        if (pages > 0 && currentPage > pages) {
          setCurrentPage(pages);
        }
      })
      .catch((e) => {
        setTableData([]);
        setTotalPages(0);
        throw new Error(e);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [client.owner, finalDate, inicialDate, limit, currentPage]);

  useEffect(() => {
    getHistoricData();
  }, [getHistoricData]);

  const handlePageChange = (nextPage) => {
    if (loading) return;
    if (nextPage < 1) return;
    if (totalPages && nextPage > totalPages) return;
    setCurrentPage(nextPage);
  };

  const pageItems = useMemo(() => {
    const maxLength = 7;
    if (!totalPages || totalPages <= 1) return [1];

    const range = (start, end) => Array.from({ length: end - start + 1 }, (_, i) => start + i);

    if (totalPages <= maxLength) return range(1, totalPages);

    const sideWidth = 1;
    const windowWidth = 3;

    const left = Math.max(currentPage - 1, 2);
    const right = Math.min(currentPage + 1, totalPages - 1);

    const showLeftEllipsis = left > sideWidth + 2;
    const showRightEllipsis = right < totalPages - (sideWidth + 1);

    const items = [1];

    if (showLeftEllipsis) {
      items.push('…');
      items.push(...range(left - (windowWidth - 2), left).filter(n => n > 1));
    } else {
      items.push(...range(2, Math.max(2, right)));
    }

    if (showRightEllipsis) {
      items.push(...range(Math.max(left, right), right).filter(n => n < totalPages));
      items.push('…');
    } else {
      items.push(...range(Math.max(left + 1, 2), totalPages - 1));
    }

    if (!items.includes(totalPages)) items.push(totalPages);

    return items.filter((v, i, arr) => i === 0 || v !== arr[i - 1]);
  }, [currentPage, totalPages]);

  const typeDescripton = {
    CREDIT: 'Crédito',
    BONUS: 'Bônus'
  };

  const methodOfPayment = {
    CREDIT_CARD: 'Cartão de Crédito',
    BOLETO: 'Boleto Bancário',
    PIX: 'Pix'
  };

  const statusDescripton = (status) => {
    switch (status) {
      case 'SUCCESS':
        return 'Sucesso';
      default:
        return status;
    }
  };

  return (
    <Card>
      <CardBody className="panel__body dashboard__health-chart-card">
        <div className="panel__btns">
          <button
            className="panel__btn"
            aria-label="panel__btn"
            type="button"
            onClick={() => getHistoricData()}
          >
            <AutorenewIcon />
          </button>
        </div>
        <div className="panel__title" style={{ marginBottom: '6px' }}>
          <h5 className="bold-text text-left">Tabela do Historico</h5>
        </div>
        {loading && (
          <div style={{ height: '24rem' }}>
            <div className="panel__refresh">
              <LoadingIcon />
            </div>
          </div>
        )}
        <Row className="">
          <Col col={12}>
            <div className="payment_info mt-3">
              {!loading && (
                <>
                  <Table bordered hover responsive className="equal-width">
                    <thead>
                      <tr className="table-header">
                        <th>Data</th>
                        <th>Status</th>
                        <th>Valor</th>
                        <th>Tipo</th>
                        <th>Meio de pagamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableData.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center">
                            Nenhum registro encontrado.
                          </td>
                        </tr>
                      ) : (
                        tableData.map((item) => (
                          <tr key={item.id}>
                            <td>{format(new Date(item.createdAt), 'dd/MM/yyyy HH:mm:ss')}</td>
                            <td>{statusDescripton(item.status)}</td>
                            <td>R$ {brMoneyMask(item.value)}</td>
                            <td>{typeDescripton[item.type]}</td>
                            <td>{methodOfPayment[item.paymentType]}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </Table>
                  {totalPages > 1 && (
                    <Pagination aria-label="pagination">
                      <PaginationItem disabled={currentPage <= 1}>
                        <PaginationLink
                          first
                          href="#"
                          onClick={(e) => { e.preventDefault(); handlePageChange(1); }}
                        />
                      </PaginationItem>

                      <PaginationItem disabled={currentPage <= 1}>
                        <PaginationLink
                          previous
                          href="#"
                          onClick={(e) => { e.preventDefault(); handlePageChange(currentPage - 1); }}
                        />
                      </PaginationItem>

                      {pageItems.map((p, idx) => (
                        typeof p === 'number' ? (
                          <PaginationItem key={p} active={p === currentPage}>
                            <PaginationLink
                              href="#"
                              className="custom-pagination"
                              onClick={(e) => { e.preventDefault(); handlePageChange(p); }}
                            >
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        ) : (
                          <PaginationItem key={`ellipsis-${idx+1}`} disabled>
                            <PaginationLink href="#" onClick={(e) => e.preventDefault()}>
                              {p}
                            </PaginationLink>
                          </PaginationItem>
                        )
                      ))}

                      <PaginationItem disabled={currentPage >= totalPages}>
                        <PaginationLink
                          next
                          href="#"
                          onClick={(e) => { e.preventDefault(); handlePageChange(currentPage + 1); }}
                        />
                      </PaginationItem>

                      <PaginationItem disabled={currentPage >= totalPages}>
                        <PaginationLink
                          last
                          href="#"
                          onClick={(e) => { e.preventDefault(); handlePageChange(totalPages); }}
                        />
                      </PaginationItem>
                    </Pagination>
                  )}
                </>
              )}
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

HistoricTable.propTypes = {
  finalDate: PropTypes.string.isRequired,
  inicialDate: PropTypes.string.isRequired
};

export default HistoricTable;
